// src/app/app.routes.ts
import { Routes } from '@angular/router';
import { PublicApartmentViewComponent } from "./public-view/apartment-public.component";
import { MainLayoutComponent } from './layout/main-layout.component';
import { MsalGuard } from "@azure/msal-angular";
import { DeveloperPageChromeFacade } from './features/developer/services/developer-page-chrome.facade';
import { companyEditPendingChangesGuard } from './features/developer/pages/company-edit/company-edit-pending-changes.guard';
import { activeSessionGuard } from './core/session/guards/active-session.guard';
import { developerContextGuard } from './core/session/guards/developer-context.guard';
import { onboardingRequiredGuard } from './core/session/guards/onboarding-required.guard';
import { opsAdminGuard } from './core/session/guards/ops-admin.guard';
import { redirectByAppStateGuard } from './core/session/guards/redirect-by-app-state.guard';

export const routes: Routes = [
  {
    path: 'public',
    children: [
      {
        path: 'apartment/:apartmentId',
        component: PublicApartmentViewComponent
      },
    ],
  },
  // 🎨 EMBED WIDGET (Public - no auth, for iframe embedding on client sites)
  {
    path: 'auth',
    children: [
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      {
        path: 'login',
        loadComponent: () =>
          import('./core/auth/login/login').then((m) => m.LoginComponent),
      },
      {
        path: 'access-denied',
        loadComponent: () =>
          import('./core/auth/access-denied/access-denied.component').then((m) => m.AccessDeniedComponent),
      },
    ],
  },
  {
    path: 'admin',
    canActivate: [MsalGuard, activeSessionGuard, opsAdminGuard],
    loadChildren: () => import('./features/admin/admin.routes').then((m) => m.ADMIN_ROUTES),
  },
  {
    path: 'onboarding',
    children: [
      {
        path: 'developer',
        canActivate: [MsalGuard, activeSessionGuard, onboardingRequiredGuard],
        loadComponent: () =>
          import('./features/onboarding/developer/onboarding-developer-page.component').then(
            (m) => m.OnboardingDeveloperPageComponent
          ),
      },
    ],
  },
  {
    path: 'home',
    component: MainLayoutComponent,
    canActivate: [MsalGuard, activeSessionGuard, developerContextGuard],
    children: [
      { path: '', loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent) },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/user/users/users').then((m) => m.UsersComponent),
      },
      // 🌐 WP SITES MANAGEMENT
      {
        path: 'wp-sites',
        loadChildren: () => import('./features/wp-sites/wp-sites.routes').then(m => m.WP_SITES_ROUTES)
      },
      // 🎨 WIDGETS CONFIGURATOR (Protected - requires auth)
      // 📚 DOCUMENTATION
      {
        path: 'docs',
        loadChildren: () => import('./features/docs/docs.routes').then(m => m.DOCS_ROUTES)
      },
      // 🖼️ MEDIA MANAGER
      {
        path: 'media',
        loadComponent: () =>
          import('./features/media/pages/media-manager/media-manager-page.component').then(
            (m) => m.MediaManagerPageComponent
          ),
      },
      // 🏗️ INVESTMENT-CENTRIC STRUCTURE
      {
        path: 'investments',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './features/investment/pages/list/investments-list.component'
              ).then((m) => m.InvestmentsListComponent),
          },
          {
            path: 'create',
            loadComponent: () =>
              import('./features/investment/pages/create/investment-create.component').then(
                (m) => m.InvestmentCreateComponent
              ),
          },
          {
            path: ':investmentId/edit',
            loadComponent: () =>
              import('./features/investment/pages/edit/investment-edit.component').then(
                (m) => m.InvestmentEditComponent
              ),
          },
          {
            path: ':investmentId',
            redirectTo: ':investmentId/view',
            pathMatch: 'full',
          },
          {
            path: ':investmentId/view',
            loadComponent: () =>
              import(
                './features/investment/pages/details/investment-view.component'
              ).then((m) => m.InvestmentViewComponent),
          },
          // 🏠 APARTMENTS WITHIN INVESTMENT
          {
            path: ':investmentId/apartments',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/apartment/pages/list/apartment-list.component').then(
                    (m) => m.ApartmentListComponent
                  ),
              },
              {
                path: 'create',
                loadComponent: () =>
                  import(
                    './features/apartment/pages/create/apartment-create.component'
                  ).then((m) => m.ApartmentCreateComponent),
              },
              {
                path: ':apartmentId/edit',
                loadComponent: () =>
                  import(
                    './features/apartment/pages/edit/apartment-edit.component'
                  ).then((m) => m.ApartmentEditComponent),
              },
              {
                path: ':apartmentId',
                loadComponent: () =>
                  import(
                    './features/apartment/pages/details/apartment-details.component'
                  ).then((m) => m.ApartmentDetailsComponent),
              },
            ],
          },
          // 🅿️ PARKINGS WITHIN INVESTMENT
          {
            path: ':investmentId/parkings',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/parking/pages/list/parkings-list.component').then(
                    (m) => m.ParkingsListComponent
                  ),
              },
              {
                path: 'create',
                redirectTo: '',
                pathMatch: 'full',
              },
              {
                path: ':parkingId/edit',
                redirectTo: '',
                pathMatch: 'full',
              },
            ],
          },
          // 🏢 BUILDINGS WITHIN INVESTMENT
          {
            path: ':investmentId/buildings',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/building/pages/list/building-list.component').then(
                    (m) => m.BuildingListComponent
                  ),
              },
            ],
          },

          {
            path: ':investmentId/ancillary-spaces',
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./features/ancillary-spaces/pages/list/ancillary-spaces-list.component').then(
                    (m) => m.AncillarySpacesListComponent
                  ),
              },
              {
                path: 'create',
                redirectTo: '',
                pathMatch: 'full',
              },
              // {
              //   path: ':parkingId',
              //   loadComponent: () =>
              //     import(
              //       './features/parking/parking-details/parking-details'
              //     ).then((m) => m.ParkingDetailsComponent),
              // },
              {
                path: ':ancillaryId/edit',
                redirectTo: '',
                pathMatch: 'full',
              },
            ],
          },
        ],
      },
      {
        path: 'developer',
        providers: [DeveloperPageChromeFacade],
        children: [
          {
            path: '',
            redirectTo: 'view',
            pathMatch: 'full',
          },
          {
            path: 'view',
            loadComponent: () =>
              import('./features/developer/pages/view/developer-view.component').then(
                (m) => m.DeveloperViewComponent
              ),
          },
          {
            path: 'companies/:companyId/edit',
            canDeactivate: [companyEditPendingChangesGuard],
            loadComponent: () =>
              import('./features/developer/pages/company-edit/company-edit.component').then(
                (m) => m.CompanyEditComponent
              ),
          },
          {
            path: 'companies',
            loadComponent: () =>
              import('./features/developer/pages/companies/developer-companies.component').then(
                (m) => m.DeveloperCompaniesComponent
              ),
          },
          {
            path: 'sales-offices',
            loadComponent: () =>
              import('./features/place/places-list/places-list.component').then(
                (m) => m.PlacesListComponent
              ),
          },
          {
            path: ':developerId',
            redirectTo: 'view',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'gov',
        children: [
          {
            path: '',
            redirectTo: 'resources/daily',
            pathMatch: 'full',
          },
          {
            path: 'resources/daily',
            loadComponent: () =>
              import(
                './features/gov2/pages/resource-daily/gov2-resource-daily.component'
              ).then((m) => m.Gov2ResourceDailyComponent),
          },
        ],
      },
      // 🔄 LEGACY REDIRECTS (for backward compatibility)
      {
        path: 'apartments',
        redirectTo: 'investments',
        pathMatch: 'full',
      },
      {
        path: 'parkings',
        redirectTo: 'investments',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [MsalGuard, redirectByAppStateGuard],
    loadComponent: () =>
      import('./core/session/session-redirect.component').then((m) => m.SessionRedirectComponent),
  },
  {
    path: '**',
    canActivate: [MsalGuard, redirectByAppStateGuard],
    loadComponent: () =>
      import('./core/session/session-redirect.component').then((m) => m.SessionRedirectComponent),
  }
];
